/**
 * Cleans a raw floor mask from the segmentation provider:
 * keeps the largest region plus any region over 5% of the image, and fills
 * holes smaller than 0.5% of the image. Works on 0/255 masks.
 */

/** 4-connected component labels for floor (`on`) or background pixels. Unlabeled pixels are -1. */
function label(mask: Uint8Array, w: number, h: number, on: boolean) {
  const n = w * h;
  const labels = new Int32Array(n);
  for (let i = 0; i < n; i++) labels[i] = (mask[i] > 127) === on ? -1 : -2;
  const sizes: number[] = [];
  const touchesBorder: boolean[] = [];
  const stack = new Int32Array(n);
  for (let start = 0; start < n; start++) {
    if (labels[start] !== -1) continue;
    const id = sizes.length;
    let size = 0;
    let border = false;
    let top = 0;
    stack[top++] = start;
    labels[start] = id;
    while (top) {
      const i = stack[--top];
      size++;
      const x = i % w;
      if (x === 0 || x === w - 1 || i < w || i >= n - w) border = true;
      if (x > 0 && labels[i - 1] === -1) (labels[i - 1] = id), (stack[top++] = i - 1);
      if (x < w - 1 && labels[i + 1] === -1) (labels[i + 1] = id), (stack[top++] = i + 1);
      if (i >= w && labels[i - w] === -1) (labels[i - w] = id), (stack[top++] = i - w);
      if (i < n - w && labels[i + w] === -1) (labels[i + w] = id), (stack[top++] = i + w);
    }
    sizes.push(size);
    touchesBorder.push(border);
  }
  for (let i = 0; i < n; i++) if (labels[i] === -2) labels[i] = -1;
  return { labels, sizes, touchesBorder };
}

export function cleanMask(mask: Uint8Array, w: number, h: number): Uint8Array {
  const total = w * h;
  const out = new Uint8Array(total);

  const fg = label(mask, w, h, true);
  if (!fg.sizes.length) return out;
  const largest = fg.sizes.indexOf(Math.max(...fg.sizes));
  const keep = fg.sizes.map((s, i) => i === largest || s > total * 0.05);
  for (let i = 0; i < total; i++) if (fg.labels[i] >= 0 && keep[fg.labels[i]]) out[i] = 255;

  const bg = label(out, w, h, false);
  const fill = bg.sizes.map((s, i) => !bg.touchesBorder[i] && s < total * 0.005);
  for (let i = 0; i < total; i++) if (bg.labels[i] >= 0 && fill[bg.labels[i]]) out[i] = 255;
  return out;
}

/**
 * Heuristic confidence when the provider gives none: plausible coverage
 * (8% to 90%) and the floor reaches the bottom quarter of the photo.
 */
export function heuristicConfidence(mask: Uint8Array, w: number, h: number): number {
  let on = 0;
  let bottom = false;
  const bottomStart = Math.floor(h * 0.75) * w;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] > 127) {
      on++;
      if (i >= bottomStart) bottom = true;
    }
  }
  const cover = on / (w * h);
  return cover >= 0.08 && cover <= 0.9 && bottom ? 0.8 : 0.3;
}

export const LOW_CONFIDENCE = 0.5;
